import { Chart, ScatterDataPoint, ChartConfiguration } from "chart.js";
import {Moment} from 'moment';

import { PropType, defineComponent, shallowRef } from 'vue';
import { NPopover, NSpin } from "naive-ui";

import ComponentBase from "~/components/component-base";

import { baseChartConfig } from '~/components/chart/chart-base.config';
import { blueColor, greenColor, purpleColor } from "~/variables";
import { defaultsDeep } from "lodash";
import { Station } from "~/api/models/station.model";
import { Measure } from "~/api/models/measure.model";

export default defineComponent({

    extends: ComponentBase,

    components: {
        NPopover,
        NSpin,
    },

    props: {
        station: { type: Object as PropType<Station>, required: true },
        measures: { type: Object as PropType<Measure[] | null>, required: false},
        since: { type: Object as PropType<Moment>, required: true},
        before: { type: Object as PropType<Moment>, required: true},
    },

    computed: {

        timeUnit() {
            const delta = this.before.diff(this.since);
            if(delta >  3 * 24 * 60 * 60 * 1000) {
                return 'day';
            } else if(delta >  2 * 60 * 60 * 1000) {
                return 'hour';
            } else {
                return 'minute';
            }
        },

        timeUnitDisplayFormat() {
            const delta = this.before.diff(this.since);
            if(delta >=  7 * 24 * 60 * 60 * 1000) {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            } else if(delta >=  3 * 24 * 60 * 60 * 1000){
                return { day: 'lll', hour: 'lll',  minute: 'HH:mm'};
            } else {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            }
        },

        datasets() {

            if(!this.measures?.length) {
                return [];
            }
            
            const rsrpData: ScatterDataPoint[] = this.measures.map(m => ({x: m.date * 1000, y: m.values.rsrp as number}));
            const rsrqData: ScatterDataPoint[] = this.measures.map(m => ({x: m.date * 1000, y: m.values.rsrq as number}));
            const rssiData: ScatterDataPoint[] = this.measures.map(m => ({x: m.date * 1000, y: m.values.rssi as number}));

            const rssiColor = greenColor;
            const rsrpColor = blueColor;
            const rsrqColor = purpleColor;

            const datasets = [
                {
                    label: `${this.$t('indicators.rssi.label')} (${this.$t('indicators.rssi.unit')})`,
                    data: rssiData,
                    borderColor: rssiColor,
                    backgroundColor: rssiColor,
                    barTickness: 20,
                    borderWidth: 1,
                    pointRadius: rssiData && rssiData.length <= 300 ? 0 : 0,
                    pointHitRadius: 10,
                }
            ];

            if(this.isLTEMStation) {
                datasets.push( {
                    label: `${this.$t('indicators.rsrq.label')} (${this.$t('indicators.rsrq.unit')})`,
                    data:  rsrqData,
                    borderColor: rsrqColor,
                    backgroundColor: rsrqColor,
                    barTickness: 20,
                    borderWidth: 1,
                    pointRadius: rsrqData && rsrqData.length <= 300 ? 0 : 0,
                    pointHitRadius: 10,
                });
                datasets.push( {
                    label: `${this.$t('indicators.rsrp.label')} (${this.$t('indicators.rsrp.unit')})`,
                    data:  rsrpData,
                    borderColor: rsrpColor,
                    backgroundColor: rsrpColor,
                    barTickness: 20,
                    borderWidth: 1,
                    pointRadius: rsrpData && rsrpData.length <= 300 ? 0 : 0,
                    pointHitRadius: 10,
                });
            }

            return datasets;
        }
    },

    data() {
        return {
            chart: null,
            config: defaultsDeep(
                {
                    options: {
                        scales: {
                            y: {
                                title: {
                                    display: true,
                                    align: 'center',
                                    color: 'white',
                                },
                            },
                        },
                    },
                },
                baseChartConfig(''),
            ) as ChartConfiguration,
            loading: false,
        };
    },

    mounted() {
        const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;
        if (!chartElement || !this.since || !this.before) {
            return;
        }

        const chartConfig = this.config;
        chartConfig.data = { datasets: this.datasets};

        //@ts-ignore
        chartConfig.options.scales.x.time.unit = this.timeUnit;
        chartConfig.options.scales.x.suggestedMin = this.since.unix() * 1000;
        chartConfig.options.scales.x.suggestedMax = this.before.unix() * 1000;
        chartConfig.options.scales.x.time.displayFormats = this.timeUnitDisplayFormat;
        this.chart = shallowRef(new Chart(chartElement.getContext('2d'), chartConfig));
    },

    methods: {

        updateChart() {
            try {
                this.chart.data = { datasets: this.datasets};
                this.chart.update();
            } catch (e) {
                console.error(e);
            }
        },
    },

    watch: {
        datasets(datasets, old) {
            if(datasets === old) {
                return;
            }

            this.updateChart();
        }
    }
});